import { Injectable, OnDestroy } from '@angular/core';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { Device } from '@capacitor/device';
import { environment } from '../../environments';
import { StateService } from './state.service';
import { HttpClient } from '@angular/common/http';

interface LocationUpdate {
  lat: number;
  lng: number;
}

@Injectable({
  providedIn: 'root',
})
export class GeolocationService implements OnDestroy {
  private locationSubject = new BehaviorSubject<LocationUpdate | null>(null);
  private trackingInterval: Subscription | null = null;
  private batteryCheckInterval: Subscription | null = null;
  private currentInterval = 0;
  private isCharging = false;
  private trackGpsLocation = false;
  private trackingFrequencyPlugged = 60000;
  private trackingFrequencyUnplugged = 60000;
  private trackingHighPrecision = false;
  private isInitialized = false;
  private initializationInProgress = false;

  private baseUrl = environment.apiHost;

  constructor(
    private platform: Platform,
    private stateService: StateService,
    private http: HttpClient
  ) {}

  initialized(): boolean {
    return this.isInitialized;
  }

  async initializeGeoLocation(): Promise<boolean> {
    try {
      this.trackGpsLocation = this.stateService.getValue('trackGpsLocation');

      this.trackingFrequencyPlugged = this.stateService.getValue(
        'trackingFrequencyPlugged'
      );
      this.trackingFrequencyUnplugged = this.stateService.getValue(
        'trackingFrequencyUnplugged'
      );
      this.trackingHighPrecision = this.stateService.getValue(
        'trackingHighPrecision'
      );

      if (!this.trackGpsLocation) {
        this.initializationInProgress = false;
        return false;
      }

      await this.setupPowerMonitoring();

      if (this.platform.ready && this.currentInterval > 0) {
        if (this.isInitialized || this.initializationInProgress) {
          return this.isInitialized;
        }
        this.initializationInProgress = true;
        await this.startTracking();
        this.isInitialized = true;
        this.initializationInProgress = false;
        return true;
      } else {
        console.error('Platform not ready');
        this.initializationInProgress = false;
        return false;
      }
    } catch (error) {
      console.error('Failed to initialize geolocation service:', error);
      return false;
    }
  }

  ngOnDestroy() {
    this.cleanupAfterTurnOff();
  }

  async cleanupAfterTurnOff(): Promise<void> {
    try {
      // Stop all tracking
      this.stopTracking();

      // Clear all subscriptions
      if (this.batteryCheckInterval) {
        this.batteryCheckInterval.unsubscribe();
        this.batteryCheckInterval = null;
      }

      // Reset all state
      this.trackGpsLocation = false;
      this.isInitialized = false;
      this.isCharging = false;
      this.currentInterval = 0;
      this.locationSubject.next(null);

      console.log('Geolocation service cleaned up after turn off');
    } catch (error) {
      console.error('Error during geolocation service cleanup:', error);
      throw error;
    }
  }

  private async setupPowerMonitoring() {
    await this.checkBatteryState(true);

    this.batteryCheckInterval = interval(30000).subscribe(() => {
      this.checkBatteryState();
    });
  }

  private async checkBatteryState(initial: boolean = false) {
    try {
      const batteryInfo = await Device.getBatteryInfo();
      const newChargingState = batteryInfo.isCharging;
      if (initial && newChargingState === this.isCharging) {
        await this.updateInterval(this.isCharging);
      }

      if (this.isCharging !== newChargingState) {
        this.isCharging = newChargingState;
        await this.updateInterval(this.isCharging);
      }
    } catch (error) {
      console.error('Error checking battery state:', error);
      throw error;
    }
  }

  private async updateInterval(isCharging: boolean) {
    const newInterval = isCharging
      ? this.trackingFrequencyPlugged
      : this.trackingFrequencyUnplugged;

    if (this.currentInterval !== newInterval) {
      this.currentInterval = newInterval;

      await this.restartTracking();
    }
  }

  private async restartTracking() {
    const wasTracking = this.isTracking();
    if (wasTracking) {
      this.stopTracking();
      await this.startTracking();
      if (!this.batteryCheckInterval) {
        await this.setupPowerMonitoring();
      }
    }
  }

  private async startTracking() {
    if (this.trackingInterval) {
      return;
    }

    // try {
    //   const permissionStatus = await Geolocation.checkPermissions();
    //   if (permissionStatus.location !== 'granted') {
    //     const permission = await Geolocation.requestPermissions({
    //       permissions: ['location', 'coarseLocation'],
    //     });
    //     if (permission.location !== 'granted') {
    //       throw new Error('Location permission not granted');
    //     }
    //   }

    //   this.trackingInterval = interval(this.currentInterval).subscribe(
    //     async () => {
    //       try {
    //         const location = await this.getCurrentLocation();
    //         await this.sendLocationUpdate(location);
    //       } catch (error) {
    //         console.error('Error updating location:', error);
    //       }
    //     }
    //   );
    //   const initialLocation = await this.getCurrentLocation();
    //   this.sendLocationUpdate(initialLocation);
    // } catch (error) {
    //   console.error('Failed to start tracking:', error);
    //   throw error;
    // }
  }

  // private async getCurrentLocation(): Promise<LocationUpdate> {
  //   const position = await Geolocation.getCurrentPosition({
  //     enableHighAccuracy: this.trackingHighPrecision || false,
  //     timeout: 1000,
  //   });

  //   return {
  //     lat: position.coords.latitude,
  //     lng: position.coords.longitude,
  //   };
  // }

  // private async sendLocationUpdate(location: LocationUpdate) {
  //   this.locationSubject.next(location);

  //   try {
  //     await this.http
  //       .post(`${this.baseUrl}/users/mobile-position`, location)
  //       .toPromise();
  //   } catch (error) {
  //     console.error('Failed to send location update:', error);
  //   }
  // }

  private stopTracking() {
    if (this.trackingInterval) {
      this.trackingInterval.unsubscribe();
      this.trackingInterval = null;
    }

    if (this.batteryCheckInterval) {
      this.batteryCheckInterval.unsubscribe();
      this.batteryCheckInterval = null;
    }
  }

  private isTracking(): boolean {
    return this.trackingInterval !== null;
  }
}
